/**
 * Bloom Universal Contentful models (hub embeds spaces + cards via includes).
 * Hub content type: `BLOOM_UNIVERSAL_CONTENT_TYPES` in `@/core/constants/contentfulTags`.
 */

/** Known space keys (`fields.spaceKey`) — also used in `/universal-space?id=`. */
export const BLOOM_SPACE_KEYS = [
  'landscape',
  'properties',
  'retail',
  'facilities',
  'hospitality',
  'education',
] as const;

export type BloomSpaceKey = (typeof BLOOM_SPACE_KEYS)[number];

/**
 * Contentful `gridSpan` on a space.
 * Large home packs these on a 12-column grid:
 * `tall` 3×2, `quarter` 3×1, `third` 4×1, `half` 6×1, `wide` 8×1, `full` 12×1.
 */
export type BloomSpaceGridSpan =
  | 'full'
  | 'wide'
  | 'half'
  | 'third'
  | 'quarter'
  | 'tall';

/** Resolved asset after Contentful includes mapping (CDN URL). */
export interface BloomUniversalAssetFields {
  readonly url: string;
}

/** Detail-row card on a space page (Contentful-shaped / fallback). */
export interface BloomUniversalSpaceCardFields {
  readonly cardKey: string;
  readonly title: string;
  readonly subtitle?: string;
  /** Optional — omit in fallback when images come only from Contentful. */
  readonly image?: BloomUniversalAssetFields;
  /** In-app path (`/guest`) or absolute URL — opens on detail-card press. */
  readonly cardHref?: string;
  readonly isActive?: boolean;
  /** Contentful display order (same on phone, tablet, and desktop). */
  readonly sortOrder?: number;
}

/**
 * Raw / fallback shape for `bloomUniversalSpace` fields.
 * Keys === Contentful field IDs. Media fields are optional (CMS-only).
 */
export interface BloomUniversalSpaceFields {
  readonly spaceKey: string;
  readonly title: string;
  readonly tileImage?: BloomUniversalAssetFields;
  readonly icon?: BloomUniversalAssetFields;
  readonly gridSpan: BloomSpaceGridSpan;
  readonly sortOrder: number;
  readonly isActive: boolean;
  readonly pageHeadline?: string;
  readonly pageBody?: string;
  readonly pageCtaLabel?: string;
  /** In-app path (`/guest`) or absolute URL — drives Discover more. */
  readonly pageCtaHref?: string;
  readonly pageHeroImage?: BloomUniversalAssetFields;
  readonly cardsSectionTitle?: string;
  readonly pageCards?: readonly BloomUniversalSpaceCardFields[];
}

/**
 * Raw / fallback shape for `bloomUniversalHub` fields.
 * Keys === Contentful field IDs. Media / chrome icon fields are optional (CMS-only).
 */
export interface BloomUniversalHubFields {
  /** Optional CMS field — hub is fetched by content type only. */
  readonly pageKey?: string;
  readonly experienceEyebrow: string;
  readonly headline: string;
  readonly subheadline: string;
  readonly ctaLabel: string;
  /** Mobile mosaic heading — Contentful `exploreTitle`. */
  readonly exploreTitle?: string;
  /** Mobile mosaic intro — Contentful `exploreSubtitle`. */
  readonly exploreSubtitle?: string;
  /**
   * In-app path (`/welcome`) or absolute URL — drives “Let's Get Started”.
   * Prefer this over legacy `ctaSpaceKey`.
   */
  readonly ctaHref?: string;
  /**
   * Legacy space-key CTA. Prefer `ctaHref` — kept for Contentful entries that
   * have not migrated yet.
   */
  readonly ctaSpaceKey?: string;
  readonly heroBackground?: BloomUniversalAssetFields;
  /** Support dial number for space-header phone button. */
  readonly phoneNumber?: string;
  readonly phoneIcon?: BloomUniversalAssetFields;
  /** Shared arrow for hub CTA + mosaic tiles. */
  readonly arrowIcon?: BloomUniversalAssetFields;
  readonly breadcrumbChevronIcon?: BloomUniversalAssetFields;
  readonly spaces: readonly BloomUniversalSpaceFields[];
}

/** UI-ready hub model (after map from Contentful-shaped fields). */
export interface BloomUniversalHubContent {
  readonly pageKey?: string;
  readonly experienceEyebrow: string;
  readonly headline: string;
  readonly subheadline: string;
  readonly ctaLabel: string;
  readonly exploreTitle?: string;
  readonly exploreSubtitle?: string;
  /** Resolved CTA target from `ctaHref` or legacy `ctaSpaceKey`. */
  readonly ctaHref?: string;
  /** From Contentful `heroBackground` — absent when using text-only fallback. */
  readonly heroBackgroundUrl?: string;
  readonly phoneNumber?: string;
  readonly phoneIconUrl?: string;
  readonly arrowIconUrl?: string;
  readonly breadcrumbChevronIconUrl?: string;
  readonly spaces: readonly BloomUniversalSpaceContent[];
}

/** UI-ready detail card on a space page. */
export interface BloomUniversalSpaceCardContent {
  readonly cardKey: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly imageUrl?: string;
  /** From Contentful `cardHref` — opens on press when set. */
  readonly cardHref?: string;
}

export interface BloomUniversalSpaceContent {
  readonly spaceKey: string;
  readonly title: string;
  readonly tileImageUrl?: string;
  readonly iconUrl?: string;
  readonly gridSpan: BloomSpaceGridSpan;
  readonly sortOrder: number;
  readonly isActive: boolean;
  readonly pageHeadline?: string;
  readonly pageBody?: string;
  readonly pageCtaLabel?: string;
  readonly pageCtaHref?: string;
  readonly pageHeroImageUrl?: string;
  readonly cardsSectionTitle?: string;
  readonly pageCards: readonly BloomUniversalSpaceCardContent[];
}

export function isBloomSpaceKey(
  value: string | undefined,
): value is BloomSpaceKey {
  if (!value) {
    return false;
  }
  return (BLOOM_SPACE_KEYS as readonly string[]).includes(value);
}
